import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { timeoutPromise } from '../../base/util/timeoutPromise';
import { getDurableItem, getPersistentItem, getPersistentJson, removeDurableItem, setDurableItem } from '../../vrs-auth/storage';
const attemptSchema = z.object({
    attemptId: z.string().uuid(), actorId: z.string().min(1), platformCallId: z.string().min(1).max(120),
    sessionId: z.string().min(1).max(128), createdAt: z.number().finite()
}).strict();
const keyFor = (actorId) => `vrs_phone_attempt:${encodeURIComponent(actorId)}`;
const persistenceError = () => new Error('The previous phone attempt could not be checked safely. Try again.');
export function getNativeVoiceActorId() {
    if (getPersistentItem('vrs_user_role') !== 'interpreter')
        return null;
    const profile = getPersistentJson('vrs_user_info');
    return typeof profile?.id === 'string' && profile.id ? profile.id : null;
}
function decode(value) {
    try {
        return attemptSchema.parse(JSON.parse(value));
    }
    catch {
        throw persistenceError();
    }
}
export async function loadNativeVoiceAttempt() {
    const actorId = getNativeVoiceActorId();
    if (!actorId)
        return null;
    const value = await timeoutPromise(getDurableItem(keyFor(actorId)), 15000);
    if (getNativeVoiceActorId() !== actorId)
        throw persistenceError();
    if (value === null)
        return null;
    const attempt = decode(value);
    if (attempt.actorId !== actorId)
        throw persistenceError();
    return attempt;
}
/** Confirm the retry identity on disk before any remote phone creation is sent. */
export async function createNativeVoiceAttempt(platformCallId, sessionId, actorId = getNativeVoiceActorId()) {
    if (!actorId)
        throw new Error('Your signed-in interpreter profile is required to start phone audio.');
    if (getNativeVoiceActorId() !== actorId)
        throw persistenceError();
    const attempt = attemptSchema.parse({ actorId, attemptId: uuidv4(), platformCallId, sessionId, createdAt: Date.now() });
    await timeoutPromise(setDurableItem(keyFor(actorId), JSON.stringify(attempt)), 15000);
    if (getNativeVoiceActorId() !== actorId)
        throw persistenceError();
    return attempt;
}
export async function clearNativeVoiceAttempt(attempt) {
    const value = await timeoutPromise(getDurableItem(keyFor(attempt.actorId)), 15000);
    if (value === null)
        return;
    const stored = decode(value);
    if (stored.attemptId !== attempt.attemptId || stored.actorId !== attempt.actorId
        || stored.platformCallId !== attempt.platformCallId || stored.sessionId !== attempt.sessionId)
        throw persistenceError();
    await timeoutPromise(removeDurableItem(keyFor(attempt.actorId)), 15000);
}
